import { useEffect, useMemo, useRef, useState } from 'react'
import {
  collection, query, orderBy, onSnapshot, where
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { workersAPI, projectsAPI, salesAPI, trainingAPI, courseProgressAPI, careersAPI, adminAuthAPI, getAdminToken, clearAdminSession } from '../services/api'
import { createAdminHandlers } from './AdminDashboard.logic.handlers'

import translations from './AdminDashboard.i18n'
import { EMPTY_WORKER, EMPTY_TRAINING, EMPTY_EQUIP, EMPTY_PROJECT, EMPTY_SALE } from './admin/constants'
const LANG_KEY = 'dashboard_lang'
const EQUIPMENT_KEY = 'equipment_items'

export function useAdminDashboard({ items = [], onAdd = () => {} }) {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || 'fr')
  const t = translations[lang]

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authChecking, setAuthChecking] = useState(true)
  const [adminEmail, setAdminEmail] = useState('')
  const [password, setPassword] = useState('')
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [authError, setAuthError] = useState('')
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('admin_tab') || 'overview')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const [workers, setWorkers] = useState([])
  const [workerForm, setWorkerForm] = useState({ ...EMPTY_WORKER })
  const [workerStatus, setWorkerStatus] = useState('')
  const [workerSearch, setWorkerSearch] = useState('')
  const [editWorker, setEditWorker] = useState(null)
  const [editForm, setEditForm] = useState({})

  const [training, setTraining] = useState([])
  const [trainingForm, setTrainingForm] = useState({ ...EMPTY_TRAINING })
  const [trainingStatus, setTrainingStatus] = useState('')

  const [uploadedItems, setUploadedItems] = useState(() => JSON.parse(localStorage.getItem(EQUIPMENT_KEY) || '[]'))
  const [equipmentForm, setEquipmentForm] = useState({ ...EMPTY_EQUIP })
  const [equipmentStatus, setEquipmentStatus] = useState('')

  const [projects, setProjects] = useState([])
  const [projectForm, setProjectForm] = useState({ ...EMPTY_PROJECT })
  const [projectStatus, setProjectStatus] = useState('')
  const [activeProject, setActiveProject] = useState(null)

  const [sales, setSales] = useState([])
  const [saleForm, setSaleForm] = useState({ ...EMPTY_SALE })
  const [saleStatus, setSaleStatus] = useState('')
  const [saleSearch, setSaleSearch] = useState('')
  const [selectedSale, setSelectedSale] = useState(null)

  const [assignments, setAssignments] = useState([])
  const [assignModal, setAssignModal] = useState(null)
  const [assignSelected, setAssignSelected] = useState(new Set())
  const [allProgress, setAllProgress] = useState([])

  const [activeCourse, setActiveCourse] = useState(null)

  const [chatSessions, setChatSessions] = useState([])
  const [chatActiveSession, setChatActiveSession] = useState(null)
  const [chatSessionMsgs, setChatSessionMsgs] = useState([])
  const [adminReply, setAdminReply] = useState('')
  const chatEndRef = useRef(null)
  const chatPollRef = useRef(null)
  const chatMsgPollRef = useRef(null)

  const [visitorLogs, setVisitorLogs] = useState([])
  const [jobListings, setJobListings] = useState([])
  const [jobApplications, setJobApplications] = useState([])
  const [careersMsgMap, setCareersMsgMap] = useState({})
  const [showJobForm, setShowJobForm] = useState(false)
  const [jobForm, setJobForm] = useState({ title: '', department: '', location: '', type: 'Full-time', description: '', requirements: '', is_active: true })
  const [editingJobId, setEditingJobId] = useState(null)

  const [itConversations, setItConversations] = useState([])
  const [itActiveWorker, setItActiveWorker] = useState(null)
  const [itMessages, setItMessages] = useState([])
  const [itReply, setItReply] = useState('')
  const itEndRef = useRef(null)

  const [accessLogs, setAccessLogs] = useState([])
  const [trackingFilter, setTrackingFilter] = useState('')

  const [adminTimeOffRequests, setAdminTimeOffRequests] = useState([])
  const [timeOffActionMsg, setTimeOffActionMsg] = useState('')

  const [adminNotifications, setAdminNotifications] = useState([])
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)


  // Restore admin session from server (not localStorage flag)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!getAdminToken()) {
        clearAdminSession()
        if (!cancelled) {
          setIsAuthenticated(false)
          setAuthChecking(false)
        }
        return
      }
      try {
        await adminAuthAPI.me()
        if (!cancelled) setIsAuthenticated(true)
      } catch {
        clearAdminSession()
        if (!cancelled) setIsAuthenticated(false)
      } finally {
        if (!cancelled) setAuthChecking(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return
    workersAPI.getAll().then(setWorkers).catch(console.error)
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    projectsAPI.getAll().then(setProjects).catch(console.error)
  }, [isAuthenticated])
  useEffect(() => {
    if (!isAuthenticated) return
    salesAPI.getAll().then(setSales).catch(console.error)
  }, [isAuthenticated])
  useEffect(() => {
    if (!isAuthenticated) return
    trainingAPI.getAll().then(setTraining).catch(console.error)
    trainingAPI.getAssignments().then(setAssignments).catch(console.error)
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'training') return
    courseProgressAPI.getAll().then(setAllProgress).catch(console.error)
  }, [activeTab, isAuthenticated])

  useEffect(() => {
    chatPollRef.current = null
    setChatSessions([])

  }, [])

  useEffect(() => {
    clearInterval(chatMsgPollRef.current)
    if (!chatActiveSession) return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    const fetchMsgs = () => {
      fetch(`${API_BASE}/visitors/chat/${chatActiveSession}`)
        .then(r => r.json())
        .then(rows => setChatSessionMsgs(rows.map(r => ({
          id: r.id, sender: r.sender, name: r.name, text: r.text, timestamp: r.sent_at
        }))))
        .catch(console.error)
    }
    fetchMsgs()
    chatMsgPollRef.current = setInterval(fetchMsgs, 4000)
    return () => clearInterval(chatMsgPollRef.current)
  }, [chatActiveSession])

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'visitors') return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    const token = getAdminToken()
    fetch(`${API_BASE}/visitors/logs`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then(r => r.json())
      .then(setVisitorLogs)
      .catch(console.error)
  }, [activeTab, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'careers') return
    careersAPI.getAllJobs().then(setJobListings).catch(() => {})
    careersAPI.getAllApplications().then(setJobApplications).catch(() => {})
  }, [activeTab, isAuthenticated])

  useEffect(() => {
    if (activeTab !== 'tracking') return
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    fetch(`${API_BASE}/workers/access-logs`, { credentials: 'include', headers: getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {} })
      .then(r => r.json())
      .then(rows => setAccessLogs(rows.map(r => ({
        id: r.id,
        workerName: r.worker_name,
        department: r.department,
        accessTime: r.access_time,
        lat: r.lat ? parseFloat(r.lat) : null,
        lng: r.lng ? parseFloat(r.lng) : null,
        device: r.device,
        ip: r.ip,
      }))))
      .catch(console.error)
  }, [activeTab])

  useEffect(() => {
    const q = query(collection(db, 'time_off_requests'), orderBy('requestedAt', 'desc'))
    return onSnapshot(q, snap => {
      setAdminTimeOffRequests(snap.docs.map(d => ({
        id: d.id, ...d.data(),
        requestedAt: d.data().requestedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })))
    }, console.error)
  }, [])

  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      where('recipientType', '==', 'admin')
    )
    return onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      setAdminNotifications(docs)
    }, console.error)
  }, [])

  useEffect(() => {
    if (!notifOpen) return
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [notifOpen])

  useEffect(() => {
    if (activeTab === 'chat') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatSessionMsgs, activeTab])

  useEffect(() => {
    if (activeTab !== 'it-support') return
    const q = query(collection(db, 'it_support_messages'), orderBy('timestamp', 'asc'))
    return onSnapshot(q, snap => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data(), timestamp: d.data().timestamp?.toDate?.()?.toISOString() || new Date().toISOString() }))

      const byWorker = {}
      msgs.forEach(m => {
        if (!byWorker[m.workerId]) byWorker[m.workerId] = { workerId: m.workerId, workerName: m.workerName, messages: [], lastAt: m.timestamp }
        byWorker[m.workerId].messages.push(m)
        if (m.timestamp > byWorker[m.workerId].lastAt) byWorker[m.workerId].lastAt = m.timestamp
      })
      setItConversations(Object.values(byWorker).sort((a, b) => b.lastAt.localeCompare(a.lastAt)))
      if (itActiveWorker) setItMessages(byWorker[itActiveWorker]?.messages || [])
    }, console.error)
  }, [activeTab, itActiveWorker])

  useEffect(() => {
    itEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [itMessages])

  const existingSlugs = useMemo(() => [...items.map(i => i.slug), ...uploadedItems.map(i => i.slug)], [items, uploadedItems])
  const previewUrls = useMemo(() => equipmentForm.images.map(f => URL.createObjectURL(f)), [equipmentForm.images])
  useEffect(() => () => previewUrls.forEach(u => URL.revokeObjectURL(u)), [previewUrls])

  const unreadCount = useMemo(() => {
    return chatSessions.filter(s => s.lastSender === 'visitor').length
  }, [chatSessions])

  const filteredWorkers = useMemo(() => {
    if (!workerSearch.trim()) return workers
    const q = workerSearch.toLowerCase()
    return workers.filter(w => w.name.toLowerCase().includes(q) || w.role.toLowerCase().includes(q) || w.department.toLowerCase().includes(q))
  }, [workers, workerSearch])

  const activeWorkersList = useMemo(() => workers.filter(w => w.status === 'active'), [workers])

  const [adminCommentText, setAdminCommentText] = useState('')

  const filteredSales = useMemo(() => {
    if (!saleSearch.trim()) return sales
    const q = saleSearch.toLowerCase()
    return sales.filter(s => {
      const hay = [s.client, s.product, s.status, s.reference, s.notes, s.description, s.invoiceUrl, s.invoice_url]
        .map(v => (v || '').toString().toLowerCase())
        .join(' ')
      return hay.includes(q)
    })
  }, [sales, saleSearch])

  const handlers = createAdminHandlers({
    password,
    setPassword,
    adminEmail,
    setIsAuthenticated,
    setAuthError,
    workerForm,
    setWorkers,
    setWorkerForm,
    setWorkerStatus,
    workers,
    setEditWorker,
    setEditForm,
    editForm,
    editWorker,
    trainingForm,
    setTraining,
    setTrainingForm,
    setTrainingStatus,
    setAssignments,
    assignments,
    training,
    assignSelected,
    setAssignModal,
    setAssignSelected,
    equipmentForm,
    existingSlugs,
    uploadedItems,
    setUploadedItems,
    onAdd,
    setEquipmentForm,
    setEquipmentStatus,
    projectForm,
    setProjects,
    setProjectForm,
    setProjectStatus,
    activeProject,
    setActiveProject,
    adminCommentText,
    setAdminCommentText,
    saleForm,
    setSales,
    setSaleForm,
    setSaleStatus,
    setSelectedSale,
    selectedSale,
    setChatActiveSession,
    setChatSessionMsgs,
    adminReply,
    chatActiveSession,
    setAdminReply,
    itReply,
    itActiveWorker,
    itConversations,
    setItReply,
    adminTimeOffRequests,
    setTimeOffActionMsg,
    setActiveTab,
    setSidebarOpen
  })

  const fetchSessions = null
  const fetchMsgs = null
  const handler = null
  return { 
    ...handlers,
    accessLogs, activeCourse, activeProject, activeTab, activeWorkersList, adminCommentText, adminNotifications, adminReply, adminTimeOffRequests, allProgress, assignModal, assignSelected, assignments, authError, careersMsgMap, chatActiveSession, chatEndRef, chatMsgPollRef, chatPollRef, chatSessionMsgs, chatSessions, editForm, editWorker, editingJobId, equipmentForm, equipmentStatus, existingSlugs, fetchMsgs, fetchSessions, previewUrls, filteredSales, filteredWorkers, handler, isAuthenticated, itActiveWorker, itConversations, itEndRef, itMessages, itReply, items, jobApplications, jobForm, jobListings, lang, notifOpen, notifRef, onAdd, password, adminEmail, setAdminEmail, authChecking, selectedApplication, setSelectedApplication, projectForm, projectStatus, projects, saleForm, saleSearch, saleStatus, sales, selectedSale, setAccessLogs, setActiveCourse, setActiveProject, setActiveTab, setAdminCommentText, setAdminNotifications, setAdminReply, setAdminTimeOffRequests, setAllProgress, setAssignModal, setAssignSelected, setAssignments, setAuthError, setCareersMsgMap, setChatActiveSession, setChatSessionMsgs, setChatSessions, setEditForm, setEditWorker, setEditingJobId, setEquipmentForm, setEquipmentStatus, setIsAuthenticated, setItActiveWorker, setItConversations, setItMessages, setItReply, setJobApplications, setJobForm, setJobListings, setLang, setNotifOpen, setPassword, setProjectForm, setProjectStatus, setProjects, setSaleForm, setSaleSearch, setSaleStatus, setSales, setSelectedSale, setShowJobForm, setSidebarOpen, setTimeOffActionMsg, setTrackingFilter, setTraining, setTrainingForm, setTrainingStatus, setUploadedItems, setVisitorLogs, setWorkerForm, setWorkerSearch, setWorkerStatus, setWorkers, showJobForm, sidebarOpen, t, timeOffActionMsg, trackingFilter, training, trainingForm, trainingStatus, unreadCount, uploadedItems, visitorLogs, workerForm, workerSearch, workerStatus, workers
  }
}
